// Recovery from stale / mis-served lazy-loaded chunks after a deploy.
//
// Two failure modes this guards against, both surfacing as a blank page with
// "'text/html' is not a valid JavaScript MIME type" in the console:
//   1. A new deploy changed the content-hashed chunk filenames, so a tab that
//      loaded the previous build tries to import a chunk that no longer exists.
//   2. During deploy propagation, Cloudflare Pages briefly serves its SPA
//      fallback (index.html, text/html) for a chunk that has not yet reached
//      the edge — and caches that response.
//
// Vite dispatches a `vite:preloadError` event on `window` when a lazily
// imported chunk fails to load. A full reload re-fetches the (no-cache)
// index.html and the current chunk set, recovering the app.

export const RELOAD_COOLDOWN_MS = 10_000;

const RELOAD_MARK = 'chunkReload:lastAttempt';

// shouldReloadForStaleChunk decides whether to trigger a reload given the
// current time and the timestamp of the previous reload attempt (or null if
// there was none). It reloads on the first failure and again only after a
// cooldown, so a reload that does not fix the failure surfaces the error
// instead of looping forever. A corrupted (NaN) prior timestamp is treated as
// "no prior attempt".
export function shouldReloadForStaleChunk(now: number, lastAttempt: number | null): boolean {
  if (lastAttempt === null || Number.isNaN(lastAttempt)) return true;
  return now - lastAttempt >= RELOAD_COOLDOWN_MS;
}

// installChunkReloadHandler wires the recovery reload to Vite's preload-error
// event. Idempotent per window is not required — main.tsx calls it once.
export function installChunkReloadHandler(win: Window = window): void {
  win.addEventListener('vite:preloadError', () => {
    // sessionStorage access is intentionally NOT wrapped in try/catch. If it
    // throws (storage blocked/full), the throw suppresses this reload, which
    // fails safe. Do NOT "harden" it by catching and treating failure as
    // lastAttempt=null: dead storage would then never persist the marker, so
    // every event would reload → the exact loop this cooldown prevents.
    const raw = win.sessionStorage.getItem(RELOAD_MARK);
    const lastAttempt = raw === null ? null : Number(raw);
    if (shouldReloadForStaleChunk(Date.now(), lastAttempt)) {
      win.sessionStorage.setItem(RELOAD_MARK, String(Date.now()));
      win.location.reload();
    }
  });
}
